import React, { useCallback, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { AppSwitcher as AppSwitcherTrigger } from '@atlaskit/navigation-system/top-nav-items';
import type { TriggerProps } from '@atlaskit/popup/types';
import { PopupTrigger } from '@atlassian/entry-points/popup-trigger';
import { createUnitTileAppSwitcherIcon } from '@atlassian/switcher/unit-tile-app-switcher-icon';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { AppSwitcherProps } from '../../common/types';
import type { AppSwitcher$key } from './__generated__/AppSwitcher.graphql';
import { appSwitcherContentEntryPoint } from './app-switcher-content.entrypoint';
import { messages } from './messages';

/**
 * __App Switcher__
 *
 * The platform default handed to `AppSwitcherSlot`. Renders the shared ADS
 * `AppSwitcher` trigger and, on click, the shared `@atlassian/switcher` panel
 * in a popup — the same pairing every audited app already builds by hand (see
 * the "Slot: App Switcher" audit linked from NAVX-3389).
 *
 * ## Platform-owned entry-point content
 *
 * Unlike other content-bearing slots, `AppSwitcherProps` deliberately has no
 * app-supplied `entryPoint`: the Switcher panel is platform-owned. The static
 * `appSwitcherContentEntryPoint` therefore supplies the entry point itself,
 * keeping its `Popup`/`AtlassianSwitcherLoader` implementation out of the
 * initial GlobalTopNavigation bundle. `PopupTrigger` preloads that content
 * code through its direct trigger ref on hover/focus/pointer-down.
 *
 * Switcher owns its built-in provider-backed data and cross-join behaviour;
 * GTN supplies the uniform joinable-sites provider. The trigger calls
 * Switcher's supported `prefetchAll` API with that same provider after click,
 * without importing either module up front. Do not instead wrap the trigger in
 * `AtlassianSwitcherPrefetchTrigger`: its default container is a `<div>`,
 * which breaks Navigation System's `TopNavStart` direct-child layout contract.
 *
 * ## The trigger's icon: reusing App Home's unit profile request
 *
 * The trigger prefers a unit-tile icon (organisation name + icon) over the
 * plain ADS glyph, sourced from the exact same `appSwitcher_unitProfile`
 * field `AppHome` already reads off `queryRef` for its own unit title (see
 * NAVX-3388, and Jira's NAVX-5411 spike this mirrors:
 * `@jira/navigation-apps__topnav-app-switcher`). This is a second `useFragment`
 * read of the same underlying field, not a second network request — Relay
 * merges fragments reachable from the same query into one operation, and the
 * `$appSwitcherProductAri`/`$skipAppSwitcherUnitProfile` variables are
 * already declared wherever `queryRef` originates.
 *
 * ## The "custom logo" suppression rule
 *
 * NAVX-3389's acceptance criteria ask to suppress the unit tile when the app
 * has a custom `AppHome` logo or title. Since App Home and App Switcher render
 * independently, the platform has no way to see what a custom `AppHomeSlot`
 * override chose to render, so this is driven by an explicit top-level
 * `hasCustomAppHomeBranding` signal (see that prop's documentation) rather
 * than anything inferred here.
 *
 * ## Feature gating
 *
 * Unlike most new platform code, this has no `fg()` check of its own —
 * matching `AppHome`'s precedent (see NAVX-3388). Jira is the only current
 * adopter and already gates the entire path that reaches `GlobalTopNavigation`
 * behind `navx-global-top-nav-jira` (see `platform/AGENTS.md`'s feature-gating
 * rule); a second gate here would be redundant. Re-introduce one if a second
 * consumer adopts this slot before Jira's rollout completes.
 */
export function AppSwitcher(props: AppSwitcherProps): JSX.Element | null {
	const {
		adminUrl,
		atlassianAccountId,
		contentEntryPoint,
		discoverySection,
		hasCustomAppHomeBranding = false,
		isSelected,
		label,
		onClick,
		productKey,
		queryRef = null,
		showFlag,
		siteId,
		subProduct,
		testId,
	} = props;
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const hasPrefetchedSwitcher = useRef(false);

	const resolvedLabel = label ?? formatMessage(messages.appSwitcher);

	// No local @argumentDefinitions: `appSwitcherProductAri`/
	// `skipAppSwitcherUnitProfile` are query-level variables already declared
	// wherever `queryRef` originates (see `AppHome`'s identical comment).
	const unitProfileData = useFragment<AppSwitcher$key>(
		graphql`
			fragment AppSwitcher on Query {
				appSwitcher_unitProfile(productAri: $appSwitcherProductAri)
					@skip(if: $skipAppSwitcherUnitProfile) {
					__typename
					... on AppSwitcherUnitProfile {
						unitName
						unitIconUrl
					}
				}
			}
		`,
		queryRef ?? null,
	);
	// Suppressed whenever App Home is showing custom branding for this
	// app/site — showing the same unit branding twice (once on App Home, once
	// on the App Switcher) would be redundant (see `hasCustomAppHomeBranding`).
	const unitProfile =
		!hasCustomAppHomeBranding &&
		unitProfileData?.appSwitcher_unitProfile?.__typename === 'AppSwitcherUnitProfile'
			? unitProfileData.appSwitcher_unitProfile
			: undefined;
	// Memoized so the icon component type stays stable across renders and
	// `AppSwitcherTrigger` doesn't unmount/remount it unnecessarily (matches
	// `AppHome`'s and Jira's NAVX-5411 spike's identical memoization).
	const icon = useMemo(
		() =>
			unitProfile
				? createUnitTileAppSwitcherIcon({
						unitName: unitProfile.unitName,
						unitIconUrl: unitProfile.unitIconUrl ?? null,
					})
				: undefined,
		[unitProfile],
	);

	// Switcher's own supported prefetch API warms both its deferred bundles and
	// its provider-backed data. It is imported only after interaction so neither
	// its code nor its provider graph enters the initial GTN bundle. Do not use
	// `AtlassianSwitcherPrefetchTrigger` here: it adds a wrapper `<div>`, breaking
	// the direct-child layout contract of `TopNavStart`.
	const prefetchSwitcher = useCallback(() => {
		if (!siteId || !productKey || hasPrefetchedSwitcher.current) {
			return;
		}

		hasPrefetchedSwitcher.current = true;
		void Promise.all([
			import(
				/* webpackChunkName: "async-navigation-global-top-navigation-atlassian-switcher" */ '@atlassian/switcher'
			),
			import(
				/* webpackChunkName: "async-navigation-global-top-navigation-app-switcher-providers" */ './app-switcher-providers'
			),
		])
			.then(([{ prefetchAll }, { joinableSitesDataProvider }]) => {
				// `prefetchAll` invokes custom joinable-sites providers without
				// parameters, while the rendered provider is keyed by `cloudId`.
				// Fetch this provider directly with the render-time parameters so
				// its argument-keyed cache is reused when the panel mounts.
				return Promise.all([
					prefetchAll({
						cloudId: siteId,
						product: productKey,
					}),
					joinableSitesDataProvider.fetchMethod({ cloudId: siteId }),
				]);
			})
			.catch(() => {
				// Preload is opportunistic. Reset so a later interaction can retry;
				// the entry-point fallback still handles an eventual open safely.
				hasPrefetchedSwitcher.current = false;
			});
	}, [productKey, siteId]);

	// Fired before the click is handed on, so the event is recorded even when a
	// consumer's `onClick` navigates away or there is no usable panel to open.
	const fireAppSwitcherClicked = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			fireEvent('ui.topNav.clicked.appSwitcher', {});
			onClick?.(event);
		},
		[fireEvent, onClick],
	);

	const renderTrigger = useCallback(
		({ ref, ...triggerProps }: TriggerProps) => (
			<AppSwitcherTrigger
				{...triggerProps}
				ref={ref}
				label={resolvedLabel}
				icon={icon}
				isSelected={isSelected ?? triggerProps['aria-expanded'] === true}
				onClick={(event) => {
					// PopupTrigger's merged ref handles entry-point code preload on
					// hover/focus/pointer-down. Warm Switcher's separate provider data
					// here without adding a wrapper node around this top-nav item.
					prefetchSwitcher();
					fireAppSwitcherClicked(event);
				}}
				testId={testId}
			/>
		),
		[fireAppSwitcherClicked, icon, isSelected, prefetchSwitcher, resolvedLabel, testId],
	);

	// Do not render a control that cannot open a usable panel. `siteId` is
	// supplied by GlobalRoot's network storage context and
	// `productKey` is a required host-app contract, so reaching this
	// branch indicates an incomplete integration rather than a valid no-data
	// state.
	if (!siteId || !productKey) {
		return null;
	}

	return (
		<PopupTrigger
			entryPoint={contentEntryPoint ?? appSwitcherContentEntryPoint}
			entryPointProps={{
				adminUrl,
				atlassianAccountId,
				cloudId: siteId,
				discoverySection,
				productKey,
				showFlag,
				subProduct,
			}}
			placement="bottom-start"
			shouldRenderToParent
			trigger={renderTrigger}
		/>
	);
}
